import { create } from "zustand"
import { persist } from "zustand/middleware"
import type { RichToolCall, PlanningStep, MentionEntity } from "@/lib/ag-ui-types"

interface AiState {
  isOpen: boolean
  activeConversationId: string | null
  isStreaming: boolean
  streamingContent: string
  mode: "sidebar" | "fullpage"
  activeToolCalls: Map<string, { name: string; status: "running" | "done" }>
  richToolCalls: Map<string, RichToolCall>
  planningSteps: Map<string, PlanningStep>
  mentionedEntities: MentionEntity[]

  // Actions
  toggle: () => void
  open: () => void
  close: () => void
  setActiveConversation: (id: string | null) => void
  setStreaming: (streaming: boolean) => void
  appendStreamContent: (content: string) => void
  resetStreamContent: () => void
  setMode: (mode: "sidebar" | "fullpage") => void

  // Simple tool calls (sidebar backward compat)
  addToolCall: (id: string, name: string) => void
  completeToolCall: (id: string) => void
  clearToolCalls: () => void

  // Rich tool calls (full-page)
  addRichToolCall: (id: string, name: string) => void
  appendToolArgs: (id: string, argsDelta: string) => void
  completeRichToolCall: (id: string, result: string) => void
  clearRichToolCalls: () => void

  // Planning steps
  startStep: (id: string, name: string) => void
  finishStep: (id: string) => void
  clearSteps: () => void

  // Mentions
  addMention: (entity: MentionEntity) => void
  removeMention: (id: string) => void
  clearMentions: () => void
}

export const useAiStore = create<AiState>()(
  persist(
    (set) => ({
      isOpen: false,
      activeConversationId: null,
      isStreaming: false,
      streamingContent: "",
      mode: "sidebar",
      activeToolCalls: new Map(),
      richToolCalls: new Map(),
      planningSteps: new Map(),
      mentionedEntities: [],

      toggle: () => set((s) => ({ isOpen: !s.isOpen })),
      open: () => set({ isOpen: true }),
      close: () => set({ isOpen: false }),
      setActiveConversation: (id) => set({ activeConversationId: id }),
      setStreaming: (streaming) => set({ isStreaming: streaming }),
      appendStreamContent: (content) =>
        set((s) => ({ streamingContent: s.streamingContent + content })),
      resetStreamContent: () =>
        set({
          streamingContent: "",
          activeToolCalls: new Map(),
          richToolCalls: new Map(),
          planningSteps: new Map(),
        }),
      setMode: (mode) => set({ mode }),

      // Simple tool calls
      addToolCall: (id, name) =>
        set((s) => {
          const next = new Map(s.activeToolCalls)
          next.set(id, { name, status: "running" })
          return { activeToolCalls: next }
        }),
      completeToolCall: (id) =>
        set((s) => {
          const next = new Map(s.activeToolCalls)
          const existing = next.get(id)
          if (existing) next.set(id, { ...existing, status: "done" })
          return { activeToolCalls: next }
        }),
      clearToolCalls: () => set({ activeToolCalls: new Map() }),

      // Rich tool calls
      addRichToolCall: (id, name) =>
        set((s) => {
          const next = new Map(s.richToolCalls)
          next.set(id, {
            id,
            name,
            status: "starting",
            args: "",
            result: null,
            startedAt: Date.now(),
            finishedAt: null,
          })
          return { richToolCalls: next }
        }),
      appendToolArgs: (id, argsDelta) =>
        set((s) => {
          const next = new Map(s.richToolCalls)
          const existing = next.get(id)
          if (existing) {
            next.set(id, { ...existing, args: existing.args + argsDelta, status: "running" })
          }
          return { richToolCalls: next }
        }),
      completeRichToolCall: (id, result) =>
        set((s) => {
          const next = new Map(s.richToolCalls)
          const existing = next.get(id)
          if (existing) {
            next.set(id, { ...existing, status: "done", result, finishedAt: Date.now() })
          }
          return { richToolCalls: next }
        }),
      clearRichToolCalls: () => set({ richToolCalls: new Map() }),

      // Planning steps
      startStep: (id, name) =>
        set((s) => {
          const next = new Map(s.planningSteps)
          next.set(id, { id, name, status: "running", startedAt: Date.now(), finishedAt: null })
          return { planningSteps: next }
        }),
      finishStep: (id) =>
        set((s) => {
          const next = new Map(s.planningSteps)
          const existing = next.get(id)
          if (existing) {
            next.set(id, { ...existing, status: "done", finishedAt: Date.now() })
          }
          return { planningSteps: next }
        }),
      clearSteps: () => set({ planningSteps: new Map() }),

      // Mentions
      addMention: (entity) =>
        set((s) => {
          if (s.mentionedEntities.some((e) => e.id === entity.id)) return s
          return { mentionedEntities: [...s.mentionedEntities, entity] }
        }),
      removeMention: (id) =>
        set((s) => ({
          mentionedEntities: s.mentionedEntities.filter((e) => e.id !== id),
        })),
      clearMentions: () => set({ mentionedEntities: [] }),
    }),
    {
      name: "okeycrm-ai",
      partialize: (state) => ({
        isOpen: state.isOpen,
        activeConversationId: state.activeConversationId,
      }),
    }
  )
)
